import type {
  AccidentCalculations,
  AccidentData,
  EnvironmentalCalculations,
  EnvironmentalData,
} from '../../types/reports.js';

/**
 * Reiner Seed-Aufbau fuer die Live-Vorschau des Unfallformulars, ausgelagert aus
 * ReportEditPage.tsx, weil er ohne Rendering testbar ist (das Projekt hat keine
 * Komponenten-Rendertests).
 *
 * C-04: kein hartkodierter KANUE-Fallback-Literal mehr -- der Wert wird falsch, sobald der VDZ
 * den Einstellungswert aendert, und widerspricht AnnualWorkingHoursProvider als einziger Quelle.
 * Fehlt annualHoursTemp auf der Meldung, bleibt das Ergebnis null.
 *
 * C-05: der Guard ist nicht mehr an hoursWorkedTotal allein gebunden. Sobald mindestens einer der
 * berechneten Werte gesetzt ist, entsteht ein Objekt -- eine Meldung mit KANUE-Wert, aber ohne
 * erfasste Stunden zeigt ihre Werte damit schon beim Oeffnen statt erst beim ersten Tippen. Der
 * null-Fall bleibt bewusst erhalten: useReportCalc unterscheidet an initialCalc !== null, ob es
 * die Seed-Baseline halten oder sofort rechnen darf.
 *
 * Nachtrag aus der Browser-Verifikation (Plan 20.1-05, Fall 3): die reine Lockerung reichte nicht.
 * Ein Seed traegt nur die PERSISTIERTEN Werte -- und weil useReportCalc ihn zugleich als Baseline
 * nimmt (dort Zeile 76), unterdrueckte ein unvollstaendiger Seed den ersten Rechenaufruf. Die
 * KANUE-Zelle erschien beim Oeffnen, die Gesamt-Zelle blieb leer bis zur ersten Feldberuehrung.
 * Seit D-01/D-03 traegt jede Meldung einen KANUE-Wert, aus dem nach A-01 immer eine Summe folgt;
 * fehlt annualHoursTotal trotzdem, ist der Seed veraltet und darf die Baseline nicht stellen.
 * null heisst dann: sofort rechnen. Das greift, weil annualHoursTemp seit C-01 Teil von
 * ACCIDENT_CALC_INPUT_KEYS ist -- der Rechenschluessel unterscheidet sich dadurch vom
 * Mount-Leerschluessel, der Effekt laeuft, und ohne Baseline feuert er den Aufruf.
 */
export function buildAccidentInitialCalc(acc: AccidentData): AccidentCalculations | null {
  const hasAnyComputedValue =
    acc.hoursWorkedTotal != null ||
    acc.accidentsLostDayTotal != null ||
    acc.lostWorkDaysTotal != null ||
    acc.annualHoursWage != null ||
    acc.annualHoursAdmin != null ||
    acc.annualHoursTemp != null ||
    acc.annualHoursTotal != null ||
    acc.annualHoursCombined != null ||
    acc.accidentRateOwn != null ||
    acc.accidentRateIndirect != null ||
    acc.accidentRateTotal != null;

  const seedWouldSuppressAPendingSum = acc.annualHoursTemp != null && acc.annualHoursTotal == null;

  if (!hasAnyComputedValue || seedWouldSuppressAPendingSum) {
    return null;
  }

  return {
    hoursWorkedTotal: acc.hoursWorkedTotal ?? null,
    accidentsLostDayTotal: acc.accidentsLostDayTotal ?? null,
    lostWorkDaysTotal: acc.lostWorkDaysTotal ?? null,
    annualHoursWage: acc.annualHoursWage ?? null,
    annualHoursAdmin: acc.annualHoursAdmin ?? null,
    annualHoursTemp: acc.annualHoursTemp ?? null,
    annualHoursTotal: acc.annualHoursTotal ?? null,
    annualHoursCombined: acc.annualHoursCombined ?? null,
    accidentRateOwn: acc.accidentRateOwn ?? null,
    accidentRateIndirect: acc.accidentRateIndirect ?? null,
    accidentRateTotal: acc.accidentRateTotal ?? null,
  };
}

/**
 * Reiner Seed-Aufbau fuer die Live-Vorschau des Umweltdaten-Formulars (Phase 30, Plan 05).
 * Analog `buildAccidentInitialCalc`: ausgelagert aus `ReportEditPage.tsx`, ohne Rendering testbar.
 *
 * Regel (C-05-Lektion aus dem Unfallformular): ergibt die Meldung keinen einzigen abgeleiteten
 * Wert -- `waterConsumption` ist `null` UND die abgeleiteten Listen (Brennstoffzeilen,
 * Kategoriesummen der Sekundaerbrennstoffe) sind `null` oder leer -- ist die Rueckgabe `null`.
 * Nur so darf `useReportCalc` sofort rechnen (der Seed wird dort
 * zugleich als Baseline genommen), statt eine leere Baseline festzuhalten und den ersten
 * Rechenaufruf zu unterdruecken. Ein leeres Array ist dabei kein Wert -- es zaehlt wie `null`,
 * sonst wuerde eine frisch angelegte, komplett unbefuellte Meldung faelschlich eine Baseline
 * bekommen.
 *
 * Andernfalls entsteht ein Objekt mit allen vier Schluesseln explizit gesetzt (`?? null` fuer die
 * fehlenden), damit kein halbleerer Seed entsteht -- genau der Fehler, den C-05 im Unfallformular
 * hinterlassen hat und dessen Symptom "die Zelle erscheint erst beim ersten Tippen" war.
 */
export function buildEnvironmentalInitialCalc(
  env: EnvironmentalData,
): EnvironmentalCalculations | null {
  const hasAnyComputedValue =
    env.waterConsumption != null ||
    (env.primaryFuelsDerived?.length ?? 0) > 0 ||
    (env.alternativeFuelsDerived?.length ?? 0) > 0 ||
    // Phase 34: eine Meldung mit vorhandenen Kategoriesummen, aber (noch) ohne abgeleitete
    // Zeilen (etwa direkt nach dem Anlegen der ersten Zeile), zaehlt ebenfalls als "hat
    // gerechnete Werte" -- sonst zeigt das Formular die Summenzeilen erst nach der ersten
    // Server-Antwort, obwohl die Meldung sie schon traegt.
    (env.alternativeFuelCategorySums?.length ?? 0) > 0 ||
    env.primaryRawMaterialsClinker != null ||
    env.primaryRawMaterialsCementMilling != null ||
    env.secondaryRawMaterialsClinker != null ||
    env.secondaryRawMaterialsCementMilling != null ||
    env.clinkerStockChange != null ||
    env.rehabilitatedAreaTotal != null ||
    // Phase 38 (D-19): mindestens ein Ofen mit gerechnetem weightedFlow zaehlt ebenfalls als
    // "hat gerechnete Werte" -- sonst zeigt ein bereits gespeichertes Rechenblatt seine Zahlen
    // erst nach der ersten Live-Vorschau-Antwort statt sofort beim Oeffnen (dieselbe C-05-Lektion
    // aus dem Unfallformular).
    (env.rotaryKilns?.some((kiln) => kiln.loadCalculation?.weightedFlow != null) ?? false);

  if (!hasAnyComputedValue) {
    return null;
  }

  return {
    waterConsumption: env.waterConsumption ?? null,
    primaryFuelsDerived: env.primaryFuelsDerived ?? null,
    alternativeFuelsDerived: env.alternativeFuelsDerived ?? null,
    alternativeFuelCategorySums: env.alternativeFuelCategorySums ?? null,
    alternativeFuelTotalSums: env.alternativeFuelTotalSums ?? null,
    primaryRawMaterialsClinker: env.primaryRawMaterialsClinker ?? null,
    primaryRawMaterialsCementMilling: env.primaryRawMaterialsCementMilling ?? null,
    secondaryRawMaterialsClinker: env.secondaryRawMaterialsClinker ?? null,
    secondaryRawMaterialsCementMilling: env.secondaryRawMaterialsCementMilling ?? null,
    clinkerStockChange: env.clinkerStockChange ?? null,
    rehabilitatedAreaTotal: env.rehabilitatedAreaTotal ?? null,
    // Phase 38 (D-19): rotaryKilnLoads ist reine Uebernahme der gespeicherten Werte, nichts wird
    // im Frontend abgeleitet -- der Seed zeigt die gespeicherten Zahlen sofort beim Oeffnen, ohne
    // ihn bliebe das Blatt leer, bis der erste Vorschauaufruf zurueckkommt (C-05-Lektion, Symptom
    // "die Zelle erscheint erst beim ersten Tippen"). Ohne Oefen: null, nicht ein leeres Array --
    // konsistent mit den uebrigen Schluesseln oben.
    rotaryKilnLoads: env.rotaryKilns?.length
      ? env.rotaryKilns.map((kiln) => ({
          weightedFlow: kiln.loadCalculation?.weightedFlow ?? null,
          totalOperatingHours: kiln.loadCalculation?.totalOperatingHours ?? null,
          loads: kiln.loadCalculation?.loads ?? null,
          // Phase 40 (D-22/D-23): derselbe Sofort-Uebernahme-Grund wie bei weightedFlow oben --
          // der gespeicherte Stand liefert massFlowCoverage sofort beim Oeffnen, ohne auf die
          // erste Live-Vorschau-Antwort zu warten.
          massFlowCoverage: kiln.loadCalculation?.massFlowCoverage ?? null,
        }))
      : null,
  };
}
